import { uiText } from '@/i18n/interface';
export const subscriptionMonths = { monthly: 1, '3months': 3, '6months': 6, '1year': 12 } as const;
export type SubscriptionType = keyof typeof subscriptionMonths;

export function isCalendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year,month,day] = value.split('-').map(Number);
  if (year < 1900 || year > 9999 || month < 1 || month > 12 || day < 1) return false;
  return day <= new Date(Date.UTC(year,month,0)).getUTCDate();
}

export function subscriptionExpiry(start: string, type: SubscriptionType): string {
  if (!isCalendarDate(start) || !Object.prototype.hasOwnProperty.call(subscriptionMonths,type)) return '';
  const [year,month,day] = start.split('-').map(Number);
  const target = month - 1 + subscriptionMonths[type];
  const targetYear = year + Math.floor(target / 12), targetMonth = target % 12;
  if (targetYear > 9999) return '';
  const targetDay = Math.min(day,new Date(Date.UTC(targetYear,targetMonth+1,0)).getUTCDate());
  return `${targetYear}-${String(targetMonth+1).padStart(2,'0')}-${String(targetDay).padStart(2,'0')}`;
}

export interface PaymentEditValues {
  amount: string; paidAmount: string; status: string; subscriptionType: SubscriptionType;
  paymentDate: string; startDate: string; endDate: string; dueDate: string;
}
export function paymentEditUpdate(values: PaymentEditValues, paymentPlan: string) {
  const amount=Number(values.amount),paid=Number(values.paidAmount);
  if (!values.amount.trim() || !values.paidAmount.trim() || !Number.isFinite(amount) || !Number.isFinite(paid) || amount<0 || paid<0) throw new Error(uiText("Enter valid, non-negative amounts."));
  if (!Object.prototype.hasOwnProperty.call(subscriptionMonths,values.subscriptionType)) throw new Error(uiText("Choose a subscription package."));
  for (const [label,date] of [['Payment date',values.paymentDate],['Subscription start',values.startDate],['Subscription expiry',values.endDate],['Next payment due',values.dueDate]]) {
    if (date && !isCalendarDate(date)) throw new Error(uiText("{{value0}} must be a valid calendar date.", { value0: label }));
  }
  if (!!values.startDate !== !!values.endDate) throw new Error(uiText("Enter both the subscription start and expiry dates."));
  if (values.startDate && values.endDate <= values.startDate) throw new Error(uiText("Subscription expiry must be after its start date."));
  if (!['pending','partial','complete','overdue'].includes(values.status)) throw new Error(uiText("Choose a payment status."));
  const total = Math.round(amount*100), amountPaid = Math.round(paid*100);
  if (!Number.isSafeInteger(total) || !Number.isSafeInteger(amountPaid)) throw new Error(uiText("The entered amount is too large."));
  const installments = Math.max(1,Number.parseInt(paymentPlan,10)||1);
  return {
    total_amount: total, amount_paid: amountPaid, status: values.status,
    subscription_type: values.subscriptionType,
    payment_date: values.paymentDate || null, start_date: values.startDate || null,
    end_date: values.endDate || null, next_due_date: values.dueDate || null,
    installments_paid: total === 0 ? (values.status === 'complete' ? installments : 0) : Math.min(installments,Math.floor(amountPaid*installments/total)),
    installment_amount: Math.ceil(total/installments),
  };
}
